import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import { budgetsApi, departmentsApi, extractList } from '../../api/facades';

const formatMkw = (amount: number): string => {
  const formatted = (amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `MK${formatted}`;
};

const BudgetPage: React.FC = () => {
  const navigate = useNavigate();
  const [year, setYear] = useState<number | ''>('');
  const [departmentId, setDepartmentId] = useState<number | ''>('');

  const { data: budgetsRes, isLoading } = useQuery({
    queryKey: ['budgets', year, departmentId],
    queryFn: () =>
      budgetsApi.list({
        year: typeof year === 'number' ? year : undefined,
        departmentId: typeof departmentId === 'number' ? departmentId : undefined,
      }),
  });

  const { data: departmentsRes } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
  });

  const budgets = extractList(budgetsRes);
  const departments = extractList(departmentsRes);

  const stats = useMemo(() => {
    const totalCeiling = budgets.reduce(
      (sum: number, line: any) => sum + Number(line.ceiling_amount || 0),
      0
    );
    const totalSpent = budgets.reduce(
      (sum: number, line: any) => sum + Number(line.spent_amount || 0),
      0
    );
    const remaining = totalCeiling - totalSpent;
    const utilization = totalCeiling > 0 ? (totalSpent / totalCeiling) * 100 : 0;

    return {
      totalCeiling,
      totalSpent,
      remaining,
      utilization,
    };
  }, [budgets]);

  const budgetLinesColumns = [
    {
      key: 'year',
      label: 'Year',
    },
    {
      key: 'department',
      label: 'Department',
      render: (r: any) => r.department?.department_name || 'All Departments',
    },
    {
      key: 'category',
      label: 'Category',
      render: (r: any) => r.category || '—',
    },
    {
      key: 'ceiling_amount',
      label: 'Budget ceiling',
      render: (r: any) => formatMkw(Number(r.ceiling_amount || 0)),
    },
    {
      key: 'spent_amount',
      label: 'Spent',
      render: (r: any) => formatMkw(Number(r.spent_amount || 0)),
    },
    {
      key: 'remaining_budget',
      label: 'Remaining',
      render: (r: any) => formatMkw(Number(r.remaining_budget || 0)),
    },
    {
      key: 'percentage_used',
      label: '% used',
      render: (r: any) => {
        const p = Number(r.percentage_used || 0);
        const color =
          p > 100 ? 'bg-red-100 text-red-800' : p >= 80 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800';
        return (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${color}`}>
            {p.toFixed(1)}%
          </span>
        );
      },
    },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 7 }).map((_, i) => currentYear - 1 + i);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PageHeader
          hideTitle
          subtitle="Track the annual corporate legal budget against actual spend."
        />
        <button
          type="button"
          onClick={() => navigate('/dashboard/budget/new')}
          className="inline-flex items-center rounded-lg bg-primary-700 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-800"
        >
          + New Budget
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Year</label>
            <select
              value={year === '' ? '' : year}
              onChange={(e) => setYear(e.target.value === '' ? '' : Number(e.target.value))}
              className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-1.5 px-3 text-sm"
            >
              <option value="">All</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Department</label>
            <select
              value={departmentId === '' ? '' : departmentId}
              onChange={(e) => setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))}
              className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-1.5 px-3 text-sm min-w-[180px]"
            >
              <option value="">All departments</option>
              {departments.map((d: any) => (
                <option key={d.id} value={d.id}>
                  {d.department_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Total Budget Ceiling
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-700 dark:text-slate-200">
            {formatMkw(stats.totalCeiling)}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Total Spent
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-700 dark:text-slate-200">
            {formatMkw(stats.totalSpent)}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Remaining Budget
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-700 dark:text-slate-200">
            {formatMkw(stats.remaining)}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            % Utilization
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-700 dark:text-slate-200">
            {stats.utilization.toFixed(1)}%
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200">
            Budgets
          </h2>
        </div>
        <DataTable
          columns={budgetLinesColumns}
          data={budgets}
          loading={isLoading}
          emptyMessage="No budgets defined."
          onRowClick={(id) => navigate(`/dashboard/budget/${id}`)}
        />
      </div>
    </div>
  );
};

export default BudgetPage;

